<template>
  <v-dialog :value="show" persistent max-width="496px" @input="close">
    <v-card class="break-dialog">
      <div class="break-dialog__header">
        <div class="break-dialog__heading">
          <div class="break-dialog__title">{{ workflowLabel(isEdit ? adminText('workflowsAll.edit_break_b7d1881', 'Edit Break') : adminText('workflowsAll.create_break_e1807d9', 'Create Break')) }}</div>
          <div class="break-dialog__subtitle">{{ adminText('workflowsAll.set_the_details_for_this_break_09bf69b', 'Set the details for this break.') }}</div>
        </div>
        <v-icon class="break-dialog__close" @click="close">close</v-icon>
      </div>

      <div class="break-dialog__form">
        <text-field v-model.trim="form.name" :validator="$v.form.name" :label="adminText('workflowsAll.break_name_a2f3742', 'Break name')" />
        <date-field
          v-model="form.day"
          :min="eventFrom"
          :max="eventTo"
          :validator="$v.form.day"
          :label="adminText('workflowsAll.day_987b9ce', 'Day')"
        />
        <v-layout>
          <v-flex class="pr-24" xs6>
            <time-field v-model="form.from" :validator="$v.form.from" :label="adminText('workflowsAll.starts_fc612a2', 'Starts')" />
          </v-flex>
          <v-flex xs6>
            <time-field
              v-model="form.to"
              :min="form.from"
              :validator="$v.form.to"
              :error-messages="{ afterStart: adminText('workflowsAll.end_time_must_be_after_the_start_time_1ea125d', 'End time must be after the start time') }"
              :label="adminText('workflowsAll.ends_91856dc', 'Ends')"
            />
          </v-flex>
        </v-layout>
        <select-field
          v-model="form.hallId"
          :items="workflowUi(hallOptions)"
          item-render="name"
          item-select="id"
          :label="adminText('workflowsAll.hall_a3b092b', 'Hall')"
        />
      </div>

      <div class="break-dialog__actions" :class="{ 'break-dialog__actions--edit': isEdit }">
        <main-button v-if="isEdit" :text="adminText('workflowsAll.cancel_77dfd21', 'Cancel')" outline @click="close" />
        <div class="break-dialog__actions-right">
          <main-button
            v-if="isEdit"
            :text="adminText('workflowsAll.delete_f6fdbe4', 'Delete')"
            color="error"
            @click="$emit('delete', breakItem)"
          />
          <main-button
            :text="workflowLabel(isEdit ? adminText('workflowsAll.update_fb91e24', 'Update') : adminText('workflowsAll.add_break_fe87b9f', 'Add break'))"
            :loading="submitInProgress"
            depressed
            @click="submit"
          />
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import moment from 'moment'
import { required } from 'vuelidate/lib/validators'
import { createWorkshop, updateWorkshop } from './../../http'
import { submitGuard } from './../../utils'

const TIME_FORMAT = 'HH:mm'
const DAY_FORMAT = 'YYYY-MM-DD'
// Leaving the hall empty means the break applies to every hall — the one kind of workshop
// a conference event allows without a hall. v-select does not handle a null item value
// well, so that option gets its own sentinel.
const ALL_HALLS = 'ALL'

// Breaks are workshops with `isBreak: true` — no lecturers, no attendees, no limit.
// Day and hall are prefilled from the list page filters when those are set.
export default {
  mixins: [WorkflowDisplay],
  name: 'break-dialog',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    eventId: {
      type: String,
      default: null,
    },
    halls: {
      type: Array,
      default: () => [],
    },
    // `YYYY-MM-DD` of the day selected on the workshops page, or null when unfiltered
    day: {
      type: String,
      default: null,
    },
    // hall selected on the workshops page, or null when unfiltered
    defaultHallId: {
      type: String,
      default: null,
    },
    eventFrom: {
      type: String,
      default: null,
    },
    eventTo: {
      type: String,
      default: null,
    },
    // the break being edited, or null when creating
    breakItem: {
      type: Object,
      default: null,
    },
  },
  validations: {
    form: {
      name: { required },
      day: { required },
      from: { required },
      to: {
        required,
        afterStart(value) {
          if (!value || !this.form.from) return true

          return moment(value, TIME_FORMAT).isAfter(moment(this.form.from, TIME_FORMAT))
        },
      },
    },
  },
  data() {
    return {
      form: { name: '', day: null, from: null, to: null, hallId: ALL_HALLS },
      submitInProgress: false,
    }
  },
  computed: {
    isEdit() {
      return Boolean(this.breakItem && this.breakItem.id)
    },
    hallOptions() {
      return [{ id: ALL_HALLS, name: 'workflowsAll.all_halls_6a9fe6f' }, ...this.halls]
    },
  },
  watch: {
    show(isOpen) {
      if (isOpen) this.resetForm()
    },
  },
  methods: {
    resetForm() {
      const b = this.breakItem

      this.form = b
        ? {
            name: b.name,
            day: moment(b.fromRaw || b.from).format(DAY_FORMAT),
            from: moment(b.fromRaw || b.from).format(TIME_FORMAT),
            to: moment(b.toRaw || b.to).format(TIME_FORMAT),
            hallId: b.hall ? b.hall.id : ALL_HALLS,
          }
        : {
            name: '',
            day: this.day,
            from: null,
            to: null,
            hallId: this.defaultHallId || ALL_HALLS,
          }

      this.$v.$reset()
    },
    // combine the chosen day with an `HH:mm` time into an ISO datetime
    toDateTime(time) {
      return moment(`${this.form.day} ${time}`, `${DAY_FORMAT} ${TIME_FORMAT}`).toISOString()
    },
    async submit() {
      await submitGuard(this, async () => {
        const values = {
          name: this.form.name,
          from: this.toDateTime(this.form.from),
          to: this.toDateTime(this.form.to),
          hallId: this.form.hallId === ALL_HALLS ? null : this.form.hallId,
        }

        if (this.isEdit) {
          await updateWorkshop({
            eventId: this.eventId,
            workshopData: { id: this.breakItem.id, ...values },
          })
        } else {
          await createWorkshop(this.eventId, {
            ...values,
            attendanceLimit: 0,
            unlimitedAttendance: false,
            isBreak: true,
          })
        }

        this.$emit('saved')
        this.close()
      })
    },
    close() {
      this.$emit('update:show', false)
    },
  },
}
</script>

<style scoped>
.break-dialog {
  border-radius: 12px;
  padding: 24px;
}
.break-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}
.break-dialog__title {
  font-family: 'Raleway';
  font-weight: 600;
  font-size: 20px;
  line-height: 1.2;
  letter-spacing: -0.6px;
  color: #0277bd;
}
.break-dialog__subtitle {
  font-size: 16px;
  line-height: 1.5;
  color: #979797;
  margin-top: 4px;
}
.break-dialog__close {
  cursor: pointer;
}
.break-dialog__form {
  margin-top: 24px;
}
.pr-24 {
  padding-right: 24px;
}
.break-dialog__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
.break-dialog__actions--edit {
  justify-content: space-between;
}
.break-dialog__actions-right {
  display: flex;
  align-items: center;
}
.break-dialog__actions-right > *:not(:last-child) {
  margin-right: 8px;
}
</style>
